import { useEffect, useMemo, useRef, useState } from "react";
import "./GuideApprovalReview.css";
import type { AdminGuideResponse } from "@rnavoyager/contracts";
import { approveAdminGuide } from "./api";
import { bindGuideReviewEvidence, readGuideReviewFile } from "./guide-review-evidence";

const confirmations = [
  "我已审阅目标、实际计划、工具调用与关键参数",
  "我已审阅实际输出、失败记录、科学判据与预算",
  "我已审阅数据披露、计算预测声明与适用局限",
] as const;

export function GuideApprovalReview({
  guide,
  onClose,
  onApproved,
}: {
  guide: AdminGuideResponse;
  onClose: () => void;
  onApproved: () => Promise<void>;
}) {
  const [files, setFiles] = useState<{ trace?: unknown; report?: unknown }>({});
  const [checks, setChecks] = useState([false, false, false]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const versions = useRef({ trace: 0, report: 0 });
  const submitting = useRef(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButton.current?.focus();
  }, []);
  const bound = useMemo(() => {
    if (files.trace === undefined || files.report === undefined) return {};
    try {
      return { evidence: bindGuideReviewEvidence(guide, files.trace, files.report) };
    } catch (cause) {
      return { problem: cause instanceof Error ? cause.message : "验收证据无法核对" };
    }
  }, [guide, files]);
  const evidence = bound.evidence;
  const canApprove =
    guide.effectiveStatus === "validated" &&
    guide.canaryCurrent &&
    evidence?.report.passed === true;

  async function selectFile(kind: "trace" | "report", file?: File) {
    const version = ++versions.current[kind];
    setFiles((current) => ({ ...current, [kind]: undefined }));
    setChecks([false, false, false]);
    setError("");
    if (!file) return;
    try {
      const value = await readGuideReviewFile(file);
      if (versions.current[kind] === version)
        setFiles((current) => ({ ...current, [kind]: value }));
    } catch (cause) {
      if (versions.current[kind] === version)
        setError(cause instanceof Error ? cause.message : "读取验收文件失败");
    }
  }
  async function submit() {
    if (submitting.current || !canApprove || !evidence || !checks.every(Boolean)) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      await approveAdminGuide(guide.definition.definitionId, {
        contentDigest: guide.definition.contentDigest,
        traceDigest: evidence.report.traceDigest,
        reviewedGoalPlanAndTools: true,
        reviewedOutputsFailuresAndBudget: true,
        reviewedDisclosureAndLimitations: true,
      });
      await onApproved();
    } catch (cause) {
      setChecks([false, false, false]);
      setError(cause instanceof Error ? cause.message : "批准失败，请重新核对当前证据");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="guide-review-title"
        className="modal-card guide-review-dialog"
        onKeyDown={(event) => {
          if (event.key === "Escape" && !busy) onClose();
        }}
      >
        <header className="modal-header">
          <h2 id="guide-review-title">审阅 Guide 验收证据</h2>
          <button ref={closeButton} type="button" disabled={busy} onClick={onClose}>
            关闭
          </button>
        </header>
        <h3>{guide.definition.displayName}</h3>
        <p>{guide.definition.description}</p>
        <p>
          选择验收运行器生成的 trace.json 和
          validation-report.json。文件在浏览器本地核对，必须匹配平台已登记的当前验收结果。
        </p>
        <div className="guide-review-files">
          <label>
            完整运行 trace.json
            <input
              type="file"
              accept=".json,application/json"
              disabled={busy}
              onChange={(event) => void selectFile("trace", event.target.files?.[0])}
            />
          </label>
          <label>
            验证报告 validation-report.json
            <input
              type="file"
              accept=".json,application/json"
              disabled={busy}
              onChange={(event) => void selectFile("report", event.target.files?.[0])}
            />
          </label>
        </div>
        {!guide.canaryCurrent && (
          <p role="status">当前验收不存在、未通过或绑定已失效，不能批准。</p>
        )}
        {(error || bound.problem) && <p role="alert">{error || bound.problem}</p>}
        {evidence && (
          <>
            <p role="status">
              文件已匹配当前登记的验收证据。验证结果：{evidence.report.passed ? "通过" : "未通过"}。
            </p>
            <dl className="guide-review-identities">
              <dt>内容摘要</dt>
              <dd>
                <code>{guide.definition.contentDigest}</code>
              </dd>
              <dt>运行证据摘要</dt>
              <dd>
                <code>{evidence.report.traceDigest}</code>
              </dd>
            </dl>
            <h3>目标与实际计划</h3>
            {evidence.trace.runnerId && (
              <p>
                验收运行器：<code>{evidence.trace.runnerId}</code>
              </p>
            )}
            <p>{guide.definition.canary.goal}</p>
            <ol>
              {evidence.trace.plan.steps.map((step) => (
                <li key={step.stepId}>
                  {step.title}（{step.status}）
                  {step.acceptanceCriteria ? `：${step.acceptanceCriteria}` : ""}
                </li>
              ))}
            </ol>
            <h3>实际工具调用与输出</h3>
            {evidence.trace.actions.map((action) => (
              <details key={action.request.actionId}>
                <summary>
                  {action.tool}.{action.function} · {action.observation.status}
                </summary>
                <p>
                  Observation：<code>{action.observation.observationId}</code>；Grant：
                  <code>{action.grant.grantId}</code>
                </p>
                <p>
                  服务：<code>{action.request.serverId}</code>；函数合同摘要：
                  <code>{action.request.functionSchemaDigest}</code>
                </p>
                <h4>关键参数</h4>
                <pre className="guide-review-output">
                  {JSON.stringify(action.request.arguments, null, 2)}
                </pre>
                <h4>计算输出</h4>
                <pre className="guide-review-output">
                  {JSON.stringify(action.observation.result, null, 2)}
                </pre>
              </details>
            ))}
            <h3>断言、失败与预算</h3>
            <ul>
              {evidence.report.assertions.map((assertion) => (
                <li key={assertion.assertionId}>
                  {assertion.assertionId}：{assertion.passed ? "通过" : "未通过"}
                </li>
              ))}
            </ul>
            <pre className="guide-review-output">
              {JSON.stringify(
                {
                  failures: evidence.trace.failures,
                  actionRejections: evidence.trace.actionRejections,
                  problems: evidence.report.problems,
                  actual: evidence.trace.observedBudget,
                  maximum: guide.definition.canary.budget,
                  cleanup: evidence.trace.cleanup,
                },
                null,
                2,
              )}
            </pre>
            <h3>最终结论</h3>
            <pre className="guide-review-output">
              {JSON.stringify(evidence.trace.final.response, null, 2)}
            </pre>
            <h3>披露与适用局限</h3>
            <p>
              本次验收使用固定非私密输入；模型与工具输出属于计算证据，不是实验验证。授权摘要：
              <code>{evidence.trace.authorizationEnvelopeDigest}</code>。
            </p>
            <ul>
              {guide.definition.guidance.limitations.map((limitation, index) => (
                <li key={index}>{limitation}</li>
              ))}
            </ul>
          </>
        )}
        <fieldset disabled={busy || !canApprove}>
          <legend>管理员明确审阅确认</legend>
          {confirmations.map((label, index) => (
            <label key={label} className="guide-review-confirmation">
              <input
                type="checkbox"
                checked={checks[index]}
                onChange={(event) =>
                  setChecks((current) =>
                    current.map((value, i) => (i === index ? event.target.checked : value)),
                  )
                }
              />
              {label}
            </label>
          ))}
        </fieldset>
        <div className="modal-actions">
          <button type="button" disabled={busy} onClick={onClose}>
            返回列表
          </button>
          <button
            type="button"
            disabled={busy || !canApprove || !checks.every(Boolean)}
            onClick={() => void submit()}
          >
            {busy ? "正在提交批准…" : "批准当前内容与验收证据"}
          </button>
        </div>
      </section>
    </div>
  );
}
